import "@/App.css";
import { useEffect, useRef, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ReactLenis } from "lenis/react";
import { Toaster } from "@/components/ui/sonner";
import { captureAttribution } from "@/lib/attribution";
import { trackPageView, trackVisit } from "@/lib/pixel";
import Landing from "@/pages/Landing";

// Homepage (Landing) stays in the main bundle for fast first paint. Everything
// else is code-split so it only downloads/parses when that route is visited.
const Admin = lazy(() => import("@/pages/Admin"));
const Standards = lazy(() => import("@/pages/Standards"));
const Membership = lazy(() => import("@/pages/Membership"));
const Quiz = lazy(() => import("@/pages/Quiz"));
const Blog = lazy(() => import("@/pages/Blog"));
const Book = lazy(() => import("@/pages/Book"));
const ManageBooking = lazy(() => import("@/pages/ManageBooking"));
const KoreanFacial = lazy(() => import("@/pages/KoreanFacial"));

const LENIS_OPTIONS = { lerp: 0.09, smoothWheel: true };

// Legacy /quiz.html links now redirect into the SPA, preserving ?concern/utm params.
function QuizHtmlRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/quiz${search}`} replace />;
}

// Old /manage links (incl. previously emailed magic links) land on the merged
// members area at the bottom of /membership, keeping the ?token intact.
function ManageRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/membership${search}#manage`} replace />;
}

// Fire a Meta Pixel PageView (with a unique event_id) on client-side route
// changes, plus a first-party visit ping on every load and navigation. The
// initial Meta PageView is fired by the base pixel in index.html, so skip the
// first render for that one only.
function AnalyticsTracker() {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    trackVisit(pathname);
    if (first.current) { first.current = false; return; }
    trackPageView();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trigger on path change; trackers are stable imports, `first` is a ref
  }, [pathname]);
  return null;
}

function App() {
  useEffect(() => {
    captureAttribution();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; captureAttribution is a stable import
  }, []);
  return (
    <HelmetProvider>
      <ReactLenis root options={LENIS_OPTIONS}>
        <div className="App">
          <BrowserRouter>
            <AnalyticsTracker />
            <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FDFBF7" }} />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/quiz" element={<Quiz />} />
                <Route path="/quiz.html" element={<QuizHtmlRedirect />} />
                <Route path="/standards" element={<Standards />} />
                <Route path="/skin/koreanfacial" element={<KoreanFacial />} />
                <Route path="/membership" element={<Membership />} />
                <Route path="/manage" element={<ManageRedirect />} />
                <Route path="/book" element={<Book />} />
                <Route path="/book/manage/:token" element={<ManageBooking />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<Blog />} />
                <Route path="/admin" element={<Admin />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster position="top-center" />
        </div>
      </ReactLenis>
    </HelmetProvider>
  );
}

export default App;
